/**
 * ValidatorFeedbackPopup — OPTIONAL post-SOAP survey for clinical validators.
 * Controlled by the parent (opens right after the validator clicks "Save Comment").
 *
 * Rules (founder directive 2026-07-24):
 *   - The survey is OPTIONAL. Identity (name/email/specialty) is captured at
 *     registration BEFORE intake, so the survey never re-asks for it and has no
 *     required fields. Validators may answer some, all, or none — or skip entirely.
 *   - All free-text is uncapped (no character limit).
 *   - On submit: shows a "Thank you" card and stores a localStorage marker so the
 *     survey never re-pops for the same encounter on this device.
 *   - `open` / `onClose` are controlled by the parent; X, backdrop and Skip all close.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    key: "q1",
    label: "Is the SOAP note clinically appropriate for this case?",
    options: [
      { value: "yes", text: "Yes" },
      { value: "no", text: "No" },
      { value: "mostly", text: "Mostly" },
    ],
    required: true,
  },
  {
    key: "q2",
    label: "Did any clinical flag get MISSED or FIRE WRONGLY?",
    options: [
      { value: "no", text: "No" },
      { value: "yes_short_note", text: "Yes (short note required)" },
    ],
    required: true,
    revealNoteOn: "yes_short_note",
    noteKey: "q2_note",
    notePlaceholder: "Briefly: which flag was missed or fired wrongly?",
  },
  {
    key: "q3",
    label: "How valuable is having past medical history + surgical history pre-organized BEFORE you see the patient?",
    options: [
      { value: "game_changer", text: "Game-changer" },
      { value: "useful", text: "Useful" },
      { value: "neutral", text: "Neutral" },
      { value: "noise", text: "Noise" },
    ],
    required: true,
  },
  {
    key: "q4",
    label: "Do you value DETERMINISTIC SOAP (same input → same output, every time, auditable, no LLM hallucination) over LLM-generated SOAP (ChatGPT / Claude / Abridge)?",
    options: [
      { value: "yes_meaningful", text: "Yes — meaningful" },
      { value: "no_llm_fine", text: "No — LLM is fine" },
      { value: "dont_know", text: "Don't know enough to judge" },
    ],
    required: true,
  },
  {
    key: "q5",
    label: "Free patient access globally — good idea for health equity? (Patient Portable Health Vault free for life; ambient clinical triage also free for individual physicians — both paid programs elsewhere like Abridge.)",
    options: [
      { value: "strongly_support", text: "Strongly support" },
      { value: "support", text: "Support" },
      { value: "neutral", text: "Neutral" },
      { value: "concerned", text: "Concerned" },
    ],
    required: true,
  },
  {
    key: "q6_free_text",
    label: "Optional — what you like, what you don't like, anything outside this list. Write as much as you want.",
    free: true,
    required: false,
    placeholder: "Share as much detail as you'd like — there's no limit.",
  },
  {
    key: "q7",
    label: "Before you see a new patient, how valuable would a structured chronological past-history briefing be — assembled from every provider the patient has ever seen, with the patient's consent under the 21st Century Cures Act?",
    options: [
      { value: "major_time_saver", text: "Major time-saver" },
      { value: "useful_some", text: "Useful for some patients" },
      { value: "neutral", text: "Neutral" },
      { value: "no_change", text: "Wouldn't change my workflow" },
    ],
    required: true,
  },
  {
    key: "q8",
    label: "If you teach medical students or residents (now or in your career), would this be useful as a case-presentation prep tool — letting the trainee see a structured patient story with traceable clinical reasoning?",
    options: [
      { value: "yes_adopt", text: "Yes — would adopt for teaching" },
      { value: "yes_possibly", text: "Yes — possibly" },
      { value: "dont_teach", text: "I don't teach" },
      { value: "not_relevant", text: "Not relevant" },
    ],
    required: true,
  },
];

const STORAGE_KEY = (encId) => `pai_validator_popup_submitted_${encId}`;

export default function ValidatorFeedbackPopup({ encounterId, open = false, onClose = () => {}, validatorNameHint, validatorEmailHint, validatorSpecialtyHint }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});

  // Reset per encounter. If this encounter was already submitted on this device,
  // show the thank-you card instead of the (optional) survey.
  useEffect(() => {
    if (!encounterId) return;
    try {
      setSubmitted(!!window.localStorage.getItem(STORAGE_KEY(encounterId)));
      setAnswers({});
      setError(null);
    } catch {
      /* no-op */
    }
  }, [encounterId]);

  // Lock body scroll while popup is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const setVal = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        encounter_id: encounterId,
        validator_name: (validatorNameHint || "").trim() || null,
        validator_email: (validatorEmailHint || "").trim() || null,
        validator_specialty: (validatorSpecialtyHint || "").trim() || null,
        q1: answers.q1 || null,
        q2: answers.q2 || null,
        q2_note: answers.q2_note || null,
        q3: answers.q3 || null,
        q4: answers.q4 || null,
        q5: answers.q5 || null,
        q6_free_text: answers.q6_free_text || null,
        q7: answers.q7 || null,
        q8: answers.q8 || null,
      };
      await axios.post(`${API}/validator-popup-feedback`, payload);
      try {
        window.localStorage.setItem(STORAGE_KEY(encounterId), new Date().toISOString());
      } catch {
        /* no-op */
      }
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Unable to save. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!encounterId || !open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="validator-popup-overlay"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Backdrop — click to close (skips the optional survey; does NOT submit) */}
      <div className="absolute inset-0 bg-black/70" aria-hidden="true" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative z-[101] w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-lg bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="validator-popup-title"
        data-testid="validator-popup-dialog"
      >
        <header className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 rounded-full p-1 hover:bg-slate-100 transition-colors"
            data-testid="validator-popup-close"
            aria-label="Close (skip the optional survey)"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 id="validator-popup-title" className="text-base font-bold text-slate-900">
            Optional Survey
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Encounter {encounterId} · ~2 minutes · Synthetic data, no PHI.
          </p>
          <p className="text-[11px] text-slate-600 italic mt-1.5 leading-snug">
            Thank you for validating. This short survey is entirely optional — answer what you like, or skip it. Your written feedback in the comment box is already saved. Your input shapes the next iteration.
          </p>
        </header>

        {submitted ? (
          <div className="px-6 py-10 text-center" data-testid="validator-popup-thanks">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-900">Thank you — your feedback was recorded.</h3>
            <p className="text-sm text-slate-600 mt-2">
              The founder will personally review every response and may reach out to discuss your input.
            </p>
            <Button
              variant="outline"
              className="mt-6"
              onClick={onClose}
              data-testid="validator-popup-close-after-submit"
            >
              Close
            </Button>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {QUESTIONS.map((q, idx) => {
              const num = idx + 1;
              if (q.free) {
                return (
                  <section key={q.key} className="rounded-md border border-slate-200 bg-white p-4" data-testid={`validator-popup-q${num}`}>
                    <div className="text-sm font-bold text-slate-900 mb-2">
                      <span className="inline-block w-6 h-6 rounded-full bg-slate-200 text-slate-700 text-xs font-bold text-center leading-6 mr-2">{num}</span>
                      {q.label}
                    </div>
                    <Textarea
                      value={answers[q.key] || ""}
                      onChange={(e) => setVal(q.key, e.target.value)}
                      placeholder={q.placeholder}
                      rows={6}
                      data-testid={`validator-popup-q${num}-input`}
                    />
                    <div className="text-[10px] text-slate-400 mt-1">{(answers[q.key] || "").length} characters</div>
                  </section>
                );
              }
              return (
                <section key={q.key} className="rounded-md border border-slate-200 bg-white p-4" data-testid={`validator-popup-q${num}`}>
                  <div className="text-sm font-bold text-slate-900 mb-3">
                    <span className="inline-block w-6 h-6 rounded-full bg-slate-200 text-slate-700 text-xs font-bold text-center leading-6 mr-2">{num}</span>
                    {q.label}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 pl-8">
                    {q.options.map((o) => (
                      <label
                        key={o.value}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm cursor-pointer transition ${
                          answers[q.key] === o.value
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"
                        }`}
                        data-testid={`validator-popup-q${num}-${o.value}`}
                      >
                        <input
                          type="radio"
                          name={q.key}
                          value={o.value}
                          checked={answers[q.key] === o.value}
                          onChange={() => setVal(q.key, o.value)}
                          className="sr-only"
                        />
                        {o.text}
                      </label>
                    ))}
                  </div>
                  {q.revealNoteOn && answers[q.key] === q.revealNoteOn && (
                    <Textarea
                      value={answers[q.noteKey] || ""}
                      onChange={(e) => setVal(q.noteKey, e.target.value)}
                      placeholder={q.notePlaceholder}
                      rows={4}
                      className="mt-3"
                      data-testid={`validator-popup-q${num}-note`}
                    />
                  )}
                </section>
              );
            })}

            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="validator-popup-error">
                {error}
              </div>
            )}
          </div>
        )}

        {!submitted && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between flex-wrap gap-2">
            <span className="text-xs text-slate-500 italic">Optional — answer what you like, or skip entirely.</span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={onClose}
                className="text-slate-600"
                data-testid="validator-popup-skip"
              >
                Skip
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="validator-popup-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {submitting ? "Submitting…" : "Submit Survey"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
